import { uniqueId } from "lodash";
import { cva, VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";
import Icon from "~/components/shared/Icon";
import { InfoTooltip } from "~/components/ClustersInsights/Chart/InfoTooltip";

export function Card({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={twMerge("animation-fade-in flex h-full flex-col gap-x-3.5 gap-y-2.5", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardLabel({
  children,
  description,
  ...props
}: { description?: string } & React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <div {...props}>
      <h2 className="my-0.5 text-base font-semibold leading-tight">{children}</h2>

      {description && <p className="m-0 text-xs font-normal text-gray-800">{description}</p>}
    </div>
  );
}

export function CardIcon({
  color,
  className,
  ...props
}: { color: string } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={twMerge("rounded-md border p-2", className)}
      style={{
        backgroundColor: `${color}10`,
        color: color,
        borderColor: `${color}20`,
      }}
      {...props}
    >
      <Icon icon="heroicons/outline/clock" className="block h-5 w-5" />
    </div>
  );
}

export function CardSection({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={twMerge("flex items-end justify-between gap-x-3", className)} {...props}>
      {children}
    </div>
  );
}

export function CardChart({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={twMerge("-m-2 mt-0 min-h-0 grow", className)} {...props}>
      {children}
    </div>
  );
}

export function CardDelta({
  formattedValue,
  color,
  value,
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & {
  formattedValue: number | string;
  color: string;
  value: number | null;
}) {
  let icon: React.ReactNode;

  if (value === null || value === 0) {
    icon = null;
  } else if (value > 0) {
    icon = <Icon icon="heroicons/outline/arrow-up" className="block h-2 w-2" />;
  } else {
    icon = <Icon icon="heroicons/outline/arrow-down" className="block h-2 w-2" />;
  }

  return (
    <span
      className={twMerge(
        "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold",
        className,
      )}
      style={{ color: color, backgroundColor: `${color}10` }}
      {...props}
    >
      {icon}
      {formattedValue}
    </span>
  );
}

const cardStatVariants = cva("flex", {
  variants: {
    size: {
      sm: "text-xs",
      lg: "text-2xl font-semibold",
    },
    layout: {
      inline: "flex-row gap-x-1.5",
      stacked: "flex-col-reverse",
    },
  },
  defaultVariants: {
    size: "sm",
    layout: "inline",
  },
});

export function CardStat({
  label,
  value,
  className,
  size,
  layout,
  ...props
}: {
  label?: string;
  value?: number | string;
} & React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardStatVariants>) {
  const labelId = label && uniqueId("stat-label-");

  return (
    <div className={twMerge(cardStatVariants({ size, layout }), className)} {...props}>
      {label && (
        <div id={labelId} className="text-xs text-gray-800">
          {label}
        </div>
      )}

      <div aria-labelledby={labelId}>{value}</div>
    </div>
  );
}

interface LabelWithTooltipProps {
  label: string | React.ReactNode;
  description?: string;
  tooltip: {
    title: string;
    description: string;
    items?: Array<string>;
  };
  className?: string;
}

export function CardLabelWithTooltip({
  label,
  description,
  tooltip,
  className,
}: LabelWithTooltipProps) {
  return (
    <div className={className}>
      <CardLabel description={description}>
        <div className="flex-0 flex items-center gap-1">
          {label}
          <div className="max-xl:hidden">
            <InfoTooltip {...tooltip} />
          </div>
        </div>
      </CardLabel>
    </div>
  );
}
